/**
 * Client-side throttle helpers for the Instagram / TikTok "search-targets"
 * autocomplete fields.
 *
 * Every autocomplete keystroke that reaches /api/{instagram,tiktok}/search-targets
 * costs a paid StarAPI upstream call (username autocomplete is currently the #1
 * StarAPI cost, ~15.7K calls/day). These pure helpers let the UI avoid firing a
 * request for input a normal human search would gain nothing from:
 *   - too-short queries (< MIN_SEARCH_CHARS)
 *   - whitespace-only / empty input
 *   - invalid handle/hashtag characters
 *   - a query already fetched this session (served from an in-memory memo)
 *
 * The visible dropdown behaviour is unchanged for someone typing a real handle
 * and pausing: results still appear after the debounce once >= MIN_SEARCH_CHARS
 * valid characters have been entered.
 */

// Minimum characters (after trimming and dropping a leading '@') before any
// network search fires. Raised from 2 -> 3: a 1-2 char query is almost never a
// real target and returns noise, so gating it removes a large slice of calls.
export const MIN_SEARCH_CHARS = 3;

// Debounce window before a settled query is searched. 400ms lets a normal typist
// finish a handle before a single request goes out (was 300ms).
export const SEARCH_DEBOUNCE_MS = 400;

// Instagram usernames / TikTok handles / hashtags: letters, digits, '_' and '.'.
const HANDLE_CHARSET = /^[a-zA-Z0-9_.]+$/;

/**
 * Pure. Normalizes raw input and returns the query to search, or null when the
 * input must NOT hit the network. Callers use the return value directly as the
 * SWR key, so `null` means "no fetch".
 *
 * - trims surrounding whitespace (so "  " / "\n" -> null)
 * - drops a single leading '@' ("@selena" -> "selena")
 * - requires >= minChars characters after normalization
 * - requires a valid handle/hashtag token (no spaces or symbols mid-string)
 */
export function normalizeSearchQuery(
  raw: string | null | undefined,
  minChars: number = MIN_SEARCH_CHARS,
): string | null {
  if (!raw) return null;
  let q = raw.trim();
  if (q.startsWith('@')) q = q.slice(1);
  if (q.length < minChars) return null;
  if (!HANDLE_CHARSET.test(q)) return null;
  return q;
}

/** Stable, case-insensitive cache key for a (type, normalized-query) pair. */
export function searchCacheKey(type: string, query: string): string {
  return `${type}::${query.toLowerCase()}`;
}

export interface SearchCache<T> {
  has(key: string): boolean;
  get(key: string): T | undefined;
  set(key: string, value: T): T;
  clear(): void;
  readonly size: number;
}

/**
 * Per-session in-memory memo (query -> results). Intended to be created once at
 * module scope so it survives component unmount/remount for the page session:
 * retyping a query already fetched this session returns instantly with NO
 * network call. Never persisted — a page reload clears it.
 */
export function createSearchCache<T = unknown>(): SearchCache<T> {
  const store = new Map<string, T>();
  return {
    has: (key) => store.has(key),
    get: (key) => store.get(key),
    set: (key, value) => {
      store.set(key, value);
      return value;
    },
    clear: () => store.clear(),
    get size() {
      return store.size;
    },
  };
}
